import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { LuArrowLeft, LuDownload, LuCreditCard, LuFileText, LuCheck, LuClock, LuPackage, LuTruck } from "react-icons/lu";
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePdf from "../Invoice/partials/InvoicePdf";
import moment from 'moment';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../utils/currency';

const PortalInvoiceDetail = () => {
    const { id, businessId } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const res = await PortalAxios.PortalRequest({
                    method: 'get',
                    url: `${baseURL}/portal/invoices/${id}`
                });
                setInvoice(res.data.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoice();
    }, [id]);

    const getStatusStyle = (status) => {
        const s = status?.toLowerCase();
        const map = {
            paid: { bg: '#d1fae5', text: '#10b981', icon: LuCheck },
            sent: { bg: '#e0f2fe', text: '#0ea5e9', icon: LuClock },
            cancelled: { bg: '#fee2e2', text: '#ef4444', icon: LuFileText },
        };
        return map[s] || { bg: '#f1f5f9', text: '#64748b', icon: LuFileText };
    };

    if (loading) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;
    if (!invoice) return <div className="text-center py-5">Invoice not found.</div>;

    const statusStyle = getStatusStyle(invoice.status);
    const fmt = (val) => formatCurrency(val, currency);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <Button variant="light" onClick={() => navigate(`/customer-side/${businessId}/invoices`)} className="rounded-3 p-2">
                        <LuArrowLeft size={20} />
                    </Button>
                    <div>
                        <h2 className="fw-bold text-dark mb-0">Invoice #{invoice._id.slice(-8).toUpperCase()}</h2>
                        <div className="text-muted small">Issued on {moment(invoice.invoiceDate).format('MMM D, YYYY')}</div>
                    </div>
                </div>
                
                <Badge pill className="px-3 py-2 fw-bold" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                    <statusStyle.icon size={14} className="me-2" />
                    {invoice.status}
                </Badge>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="text-muted small fw-bold text-uppercase mb-2">From</div>
                                <div className="fw-bold text-dark fs-5">{invoice.business?.name}</div>
                                <div className="text-muted small">
                                    {invoice.business?.location}<br />
                                    {invoice.business?.contact?.email}
                                </div>
                            </div>
                            <div className="col-md-6 text-md-end">
                                <div className="text-muted small fw-bold text-uppercase mb-2">To</div>
                                <div className="fw-bold text-dark fs-5">{invoice.customer?.name}</div>
                                <div className="text-muted small">
                                    {invoice.customer?.email}<br />
                                    {formatPhoneNumber(invoice.customer?.phone)}
                                </div>
                            </div>
                        </div>
                        
                        <hr className="my-4" />
                        
                        <div className="table-responsive">
                            <Table className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-0 py-3 text-muted small fw-bold">ITEM</th>
                                        <th className="py-3 text-muted small fw-bold text-center">QTY</th>
                                        <th className="py-3 text-muted small fw-bold text-end">RATE</th>
                                        <th className="pe-0 py-3 text-muted small fw-bold text-end">TOTAL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.productDetail?.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="ps-0 py-3 fw-semibold small">{item.name || item.productName}</td>
                                            <td className="py-3 text-center small">{item.quantity}</td>
                                            <td className="py-3 text-end small">{fmt(item.unitPrice || item.rate)}</td>
                                            <td className="pe-0 py-3 text-end fw-bold small">{fmt(item.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        
                        <div className="mt-4 pt-4 border-top">
                            <Row className="justify-content-end">
                                <Col md={5}>
                                    <div className="d-flex justify-content-between mb-2 small text-muted">
                                        <span>Subtotal</span>
                                        <span>{fmt(invoice.netAmount)}</span>
                                    </div>
                                    {invoice.appliedTaxes?.map((tax, i) => (
                                        <div key={i} className="d-flex justify-content-between mb-2 small text-muted">
                                            <span>{tax.name} ({tax.percentage}%)</span>
                                            <span>+{fmt(tax.amount)}</span>
                                        </div>
                                    ))}
                                    {invoice.appliedDiscounts?.map((d, i) => (
                                        <div key={i} className="d-flex justify-content-between mb-2 small text-danger">
                                            <span>{d.name}</span>
                                            <span>-{fmt(d.amount)}</span>
                                        </div>
                                    ))}
                                    <div className="d-flex justify-content-between align-items-center rounded-3 px-3 py-2 mt-3" style={{ background: "#28B0C410", border: "1px solid #28B0C420" }}>
                                        <span className="fw-bold">Grand Total</span>
                                        <span className="fw-bold fs-4" style={{ color: "#28B0C4" }}>{fmt(invoice.totalAmount)}</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Card>
                    
                    {invoice.notes && (
                        <Card className="border-0 shadow-sm rounded-4 p-4">
                            <h6 className="fw-bold text-dark mb-3">Notes</h6>
                            <p className="text-muted small mb-0">{invoice.notes}</p>
                        </Card>
                    )}
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
                        <h6 className="fw-bold text-dark mb-3">Actions</h6>
                        <div className="d-grid gap-2">
                            {invoice.status?.toLowerCase() !== 'paid' && invoice.paymentUrl && (
                                <Button 
                                    href={invoice.paymentUrl}
                                    className="rounded-3 py-2 fw-bold text-white border-0 d-flex align-items-center justify-content-center gap-2"
                                    style={{ background: "#28B0C4" }}
                                >
                                    <LuCreditCard size={18} /> Pay Invoice
                                </Button>
                            )}
                            <Button variant="light" className="rounded-3 py-2 small d-flex align-items-center justify-content-center gap-2">
                                <LuDownload size={18} />
                                <PDFDownloadLink
                                    document={<InvoicePdf data={invoice} />}
                                    fileName={`invoice_${invoice._id}.pdf`}
                                    style={{ textDecoration: "none", color: "inherit" }}
                                >
                                    {({ loading }) => (loading ? "Preparing..." : "Download PDF")}
                                </PDFDownloadLink>
                            </Button>
                        </div>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4 p-4">
                        <h6 className="fw-bold text-dark mb-3">Invoice Info</h6>
                        <div className="d-flex justify-content-between mb-2 extra-small text-muted">
                            <span>Issue Date</span>
                            <span className="text-dark">{moment(invoice.invoiceDate).format('MMM D, YYYY')}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2 extra-small text-muted">
                            <span>Due Date</span>
                            <span className="text-dark">{moment(invoice.invoiceDueDate).format('MMM D, YYYY')}</span>
                        </div>
                        <div className="d-flex justify-content-between extra-small text-muted">
                            <span>Currency</span>
                            <span className="text-dark">{invoice.currency}</span>
                        </div>
                    </Card>

                    {invoice.shipment && (
                        <Card className="border-0 shadow-sm rounded-4 p-4 mt-4">
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <LuTruck size={20} style={{ color: "#28B0C4" }} />
                                <h6 className="fw-bold text-dark mb-0">Shipping Status</h6>
                            </div>
                            <div className="mb-3">
                                <div className="text-muted small">Tracking Code</div>
                                <div className="fw-bold text-dark">{invoice.shipment.trackingCode}</div>
                            </div>
                            <div className="mb-4">
                                <div className="text-muted small">Status</div>
                                <div className="fw-bold text-dark">{invoice.shipment.status}</div>
                            </div>
                            <Button 
                                as={Link} 
                                to={`/track/${invoice.shipment.trackingCode}`} 
                                className="w-100 rounded-3 py-2 text-white border-0"
                                style={{ background: "#28B0C4" }}
                            >
                                Track Package
                            </Button>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default PortalInvoiceDetail;
